import React, { Fragment, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import coinIcon from "../../images/site_logo/TC-Logo-icon.png";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import { API_URL } from "../../constant";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import ReCAPTCHA from "react-google-recaptcha";

const SuccessMessage = ({ message, loginData, handleDismissMessage }) => {
  if (!message.text || message.type !== "success") return null;
  return (
    <div style={{ textAlign: "center", padding: "12px", borderRadius: "8px", margin: "10px 0", fontWeight: "500", fontSize: "0.9rem", backgroundColor: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9" }}>
      {message.text}
      {loginData && (
        <div style={{ marginTop: "10px" }}>
          <a href="/member/wallet" style={{ display: "inline-block", padding: "8px 20px", backgroundColor: "#1a5276", color: "#fff", borderRadius: "6px", textDecoration: "none", fontWeight: "600", fontSize: "0.85rem" }}>
            Open My Wallet
          </a>
        </div>
      )}
      <button onClick={handleDismissMessage} style={{ background: "none", border: "none", color: "inherit", marginLeft: "10px", cursor: "pointer", float: "right", fontSize: "16px" }} title="Dismiss">×</button>
    </div>
  );
};

const MemberLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [loginData, setLoginData] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const autoDismissRef = useRef(null);

  const onCaptchaChange = (token) => setCaptchaToken(token);

  const clearMessageAfterDelay = (delay = 10000) => {
    if (autoDismissRef.current) clearTimeout(autoDismissRef.current);
    autoDismissRef.current = setTimeout(() => { setMessage({ text: "", type: "" }); setLoginData(null); }, delay);
  };

  const handleDismissMessage = () => {
    if (autoDismissRef.current) { clearTimeout(autoDismissRef.current); autoDismissRef.current = null; }
    setMessage({ text: "", type: "" });
    setLoginData(null);
  };

  const validateLoginForm = () => {
    if (!email) { setMessage({ text: "Email is required.", type: "error" }); return false; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { setMessage({ text: "Please enter a valid email.", type: "error" }); return false; }
    if (!password) { setMessage({ text: "Password is required.", type: "error" }); return false; }
    if (password.length < 8) { setMessage({ text: "Email or Password incorrect.", type: "error" }); return false; }
    if (!captchaToken) { setMessage({ text: "Captcha verification is required.", type: "error" }); return false; }
    return true;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateLoginForm()) return;
    if (autoDismissRef.current) { clearTimeout(autoDismissRef.current); autoDismissRef.current = null; }
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/member/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();

      if (!response.ok) {
        setMessage({ text: data.message || "Login failed. Please check your credentials.", type: "error" });
        clearMessageAfterDelay(10000);
        setIsLoading(false);
        return;
      }

      // Login successful
      localStorage.setItem("auth_token", data.access_token);
      localStorage.setItem("user_type", "member");
      setLoginData(data);
      localStorage.setItem("user", JSON.stringify(data.user || { name: data.user?.name || email.split("@")[0], email }));
      sessionStorage.setItem("just_logged_in", "true");
      setMessage({ text: "Login successful! Redirecting to your wallet...", type: "success" });
      clearMessageAfterDelay(15000);
      setEmail("");
      setPassword("");
      setPasswordVisible(false);

      // Redirect to member wallet
      setTimeout(() => { navigate("/member/wallet"); }, 1500);
    } catch (error) {
      console.error("Login error:", error);
      setMessage({ text: "Network error. Please check your connection and try again.", type: "error" });
      clearMessageAfterDelay(10000);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { return () => { if (autoDismissRef.current) clearTimeout(autoDismissRef.current); }; }, []);

  return (
    <Fragment>
      <div className="index_ico page_wrapper" style={{ backgroundColor: "#0a3d5c", color: "#fff", minHeight: "100vh" }}>
        <Header />
        <main className="page_content">
          {/* Hero Section */}
          <section style={{ padding: "clamp(30px, 5vw, 60px) 0", textAlign: "center" }}>
            <div className="container">
              <h2 style={{ fontWeight: "bold", fontSize: "clamp(1.3rem, 4vw, 2.2rem)", color: "#fff", textShadow: "0 2px 10px rgba(0,0,0,0.3)", marginBottom: "8px" }}>
                Welcome to TopCoin UK Member Portal
              </h2>
              <p style={{ color: "rgba(255,255,255,0.9)", fontSize: "clamp(0.85rem, 2.5vw, 1.1rem)", maxWidth: "700px", margin: "0 auto 10px", lineHeight: "1.6" }}>
                Access your Member wallet, manage your account and explore exclusive membership benefits.
              </p>
            </div>
          </section>

          {/* Login Section */}
          <section className="register_section section_decoration py-5" style={{ minHeight: "0px" }}>
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-lg-4 col-md-8 col-sm-10" id="member-login">
                  <div className="bg-white text-center shadow p-4 mx-auto" style={{ color: "#000", borderRadius: "20px", maxWidth: "500px", marginBottom: "40px", padding: "clamp(20px, 4vw, 30px)" }}>

                    <div className="d-flex justify-content-center mb-0">
                      <img src={coinIcon} alt="TopCoin UK" style={{ width: "clamp(80px, 15vw, 115px)", height: "clamp(80px, 15vw, 115px)" }} />
                    </div>

                    <h4 className="fw-bold mb-1" style={{ background: "linear-gradient(90deg, #1a5276 30%, #2980b9 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", display: "inline-block", fontSize: "clamp(1rem, 2.5vw, 1.2rem)" }}>
                      Member - SIGN IN
                    </h4>
                    <h3 className="sub-title mb-2" style={{ fontSize: "clamp(0.9rem, 2.5vw, 1rem)" }}>
                      Enter your Email and Password
                    </h3>

                    {/* Success Message */}
                    <SuccessMessage message={message} loginData={loginData} handleDismissMessage={handleDismissMessage} />

                    {/* Error Message */}
                    {message.text && message.type !== "success" && (
                      <div style={{ textAlign: "center", padding: "7px", borderRadius: "8px", margin: "10px 0", fontWeight: "500", fontSize: "0.9rem", backgroundColor: message.type === "error" ? "#ffebee" : "#e8f5e9", color: message.type === "error" ? "#c62828" : "#2e7d32", border: message.type === "error" ? "1px solid #ffcdd2" : "1px solid #c8e6c9" }}>
                        {message.text}
                        <button onClick={handleDismissMessage} style={{ background: "none", border: "none", color: "inherit", marginLeft: "10px", cursor: "pointer", float: "right", fontSize: "16px" }} title="Dismiss">×</button>
                      </div>
                    )}

                    {/* Login Form */}
                    <form onSubmit={handleLogin} className="text-start mb-4">
                      <div className="mb-1">
                        <label className="form-label fw-semibold mb-0" style={{ color: "#052C65", fontSize: "clamp(0.8rem, 2vw, 0.9rem)" }}>Email</label>
                        <input type="email" maxLength={255} className="form-control bg-white" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={isLoading}
                          style={{ borderRadius: "8px", padding: "12px", border: "1px solid #ccc", color: "black", fontSize: "16px" }} />
                      </div>

                      <div className="mb-0 position-relative">
                        <label className="form-label fw-semibold mb-0" style={{ color: "#052C65", fontSize: "clamp(0.8rem, 2vw, 0.9rem)" }}>Password</label>
                        <div className="position-relative">
                          <input type={passwordVisible ? "text" : "password"} maxLength={50} className="form-control bg-white" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={isLoading}
                            style={{ borderRadius: "8px", padding: "12px", border: "1px solid #ccc", color: "black", paddingRight: "40px", fontSize: "16px" }} />
                          <span onClick={() => setPasswordVisible(!passwordVisible)} style={{ position: "absolute", top: "50%", right: "12px", transform: "translateY(-50%)", cursor: "pointer", color: "#666", zIndex: 2, height: "20px", display: "flex", alignItems: "center" }}>
                            {passwordVisible ? <FaEye size={18} /> : <FaEyeSlash size={18} />}
                          </span>
                        </div>
                      </div>

                      {/* Forgot Password */}
                      <div className="mb-2 text-end">
                        <a href="/forgot-password-m" style={{ color: "#1a5276", fontWeight: "500", textDecoration: "none", fontSize: "clamp(0.8rem, 2vw, 0.9rem)" }}>
                          Forgot Password?
                        </a>
                      </div>

                      {/* reCAPTCHA */}
                      <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", flexDirection: "column", transform: "scale(0.77)", transformOrigin: "center" }}>
                        <ReCAPTCHA sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI" onChange={onCaptchaChange} />
                      </div>

                      <button type="submit" className="w-100 text-white fw-bold py-2" disabled={isLoading}
                        style={{ backgroundColor: isLoading ? "#666" : "#1a5276", borderRadius: "8px", border: "none", opacity: isLoading ? 0.7 : 1, cursor: isLoading ? "not-allowed" : "pointer", padding: "12px", fontSize: "clamp(0.9rem, 2.5vw, 1rem)" }}>
                        {isLoading ? "Logging in..." : "SIGN IN"}
                      </button>
                    </form>

                    {/* Register Link */}
                    <div style={{ textAlign: "center", marginTop: "8px" }}>
                      <span style={{ color: "#666", fontSize: "0.9rem" }}>Don't have a member account? </span>
                      <a href="/member-signup" style={{ color: "#1a5276", fontWeight: "700", textDecoration: "none", fontSize: "0.9rem" }}>
                        Create Account
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </Fragment>
  );
};

export default MemberLoginPage;
